import React, {useEffect} from 'react'
import {formatDate, formatDateTime} from '../../../../utils/dayJs/format'
import {formatMask} from '../../../../utils/formatMonetaryToBr'
import {
  Billing,
  creditCardPaymentResponse,
  Installments,
  InstallmentsRecurrence,
} from '../../core/_models'

import {MenuComponent} from '../../../../_metronic/assets/ts/components'
import {PageOverlayer} from '../../../../components/PageOverlayer'
import useAuthorization from '../../../../hooks/useAuthorization'

const PAYMENT_OPTION_CATEGORY_BOLETO = 1

interface BillingDetailContentProps {
  billing: Billing | null
  options: {
    value: string
    label: string
    status: number[]
    visible?: (installment: InstallmentsRecurrence) => boolean
  }[]
  verifyAction: (action, installment, billing) => void
  openViewCollapse: string | null
  isRecurrencePayment?: boolean
  dataViewCielo?: creditCardPaymentResponse | undefined
  loading?: boolean
}

const BillingDetailContent = ({
  billing,
  options,
  verifyAction,
  openViewCollapse,
  isRecurrencePayment = true,
  dataViewCielo,
  loading,
}: BillingDetailContentProps) => {
  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  const badColor = [
    {
      name: 'Pendente',
      color: 'badge badge-warning',
    },
    {
      name: 'Pago',
      color: 'badge badge-success',
    },
    {
      name: 'Cancelado',
      color: 'badge badge-danger',
    },
    {
      name: 'Vencido',
      color: 'badge badge-warning',
    },
    {
      name: 'Substituído',
      color: 'badge badge-info',
    },
  ]

  // Boleto parcelado: pagamento manual por parcela, com vencimento e troca de forma de pagamento
  const isBoleto = billing?.paymentOption?.categoryId === PAYMENT_OPTION_CATEGORY_BOLETO
  const installmentPayableStatus = isBoleto ? [1, 4] : [1]
  // Pagamento e troca da forma de pagamento da parcela só com o billing pago ou vencido (inadimplente)
  const isBillingPaidOrExpired = ['Pago', 'Vencido'].includes(String(billing?.status))
  // Baixa e troca da forma de pagamento de parcela são do financeiro
  const isAuthorizedToUpdateInstallment = useAuthorization('billings.update_installment_status')
  const canPayInstallment = isBillingPaidOrExpired && isAuthorizedToUpdateInstallment
  const canChangeInstallmentPayment =
    isBoleto && isBillingPaidOrExpired && isAuthorizedToUpdateInstallment
  // Parcela substituída por cobrança avulsa pendente no cartão de crédito: link de pagamento
  const canCopyInstallmentPaymentLink = (installment: Installments) =>
    installment.statusId === 5 &&
    installment.settledBilling?.status === 'Pendente' &&
    installment.settledBilling?.paymentOption?.id === 2
  // A troca segue a ordem das parcelas: só a primeira parcela em aberto (pendente ou vencida)
  const firstOpenInstallment = Math.min(
    ...(billing?.installments ?? [])
      .filter((item) => [1, 4].includes(item.statusId))
      .map((item) => item.installment)
  )

  return (
    <>
      {isRecurrencePayment && (
        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-5 mt-5'>
          <thead>
            <tr className='fw-bold text-muted'>
              <th className='min-w-120px'>Preço</th>
              <th className='min-w-120px'>Dt de vencimento</th>
              <th className='min-w-100'>Dt de processamento</th>
              <th className='min-w-100'>Dt próxima tentativa</th>
              <th className='min-w-120px'>Parcela</th>
              <th className='min-w-120px'>Status</th>
              <th className='min-w-80px'>Ações</th>
            </tr>
          </thead>
          <tbody>
            {billing?.recurrencePayment?.installments?.map((installment, index) => (
              <React.Fragment key={`reactFragmentAccordionItem-${index}`}>
                <tr className='accordion-item' key={`accordionItem-${index}`}>
                  <td>
                    <b>{formatMask(installment.amount)}</b>
                  </td>
                  <td>
                    <b>{formatDate(installment.dueDate)}</b>
                  </td>
                  <td>
                    <b>{installment?.paidAt ? formatDateTime(installment.paidAt) : ''}</b>
                  </td>
                  <td>
                    <b>{installment?.next_attempt_date}</b>
                  </td>
                  <td>
                    <b>{installment.installmentsNumber}</b>
                  </td>
                  <td>
                    <span className={`badge badge-${installment.status.color}`}>
                      {installment.status.i18n}
                    </span>
                  </td>
                  <td>
                    <a
                      href='#'
                      className='btn btn-color-muted btn-active-color-danger menu-dropdown'
                      data-kt-menu-trigger='click'
                      data-kt-menu-placement='bottom-end'
                    >
                      <i className='ki-duotone ki-gear me-5 fs-1'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                        <span className='path3'></span>
                        <span className='path4'></span>
                      </i>
                    </a>
                    <div
                      className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-danger fw-semibold fs-7 w-auto py-2'
                      data-kt-menu='true'
                      data-popper-placement='bottom-end'
                    >
                      <div className='menu-item px-3'>
                        {options.map(
                          (option, index) =>
                            option.status.includes(installment.status.id) &&
                            (!option.visible || option.visible(installment)) && (
                              <span
                                key={`billingDetailContentOption${index}`}
                                className='menu-link px-3'
                                onClick={() => verifyAction(option.value, installment, null)}
                                style={{
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {option.label}
                              </span>
                            )
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr
                  className={`accordion-collapse collapse ${
                    openViewCollapse == installment.id ? 'show' : ''
                  }`}
                  key={`second-tr-${index}`}
                >
                  <td colSpan={6}>
                    <div className='table-responsive'>
                      <table className='table table-row-dashed table-row-gray-300'>
                        <thead>
                          <tr key={`three-tr-head-${index}`} className='fw-bold'>
                            <th>Tipo</th>
                            <th>Dt processamento</th>
                            <th>N Cartão</th>
                            <th>Bandeira</th>
                            <th>Status</th>
                            <th>Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {installment.creditCardPayments?.map((payment, index) => (
                            <tr key={`three-tr-${index}`}>
                              <td>Tentativa</td>
                              <td>{formatDateTime(payment.createdAt)}</td>
                              <td>{'**** ' + payment?.creditCard?.lastNumbers}</td>
                              <td>{payment?.creditCard?.brand?.description ?? ''}</td>

                              <td>{payment.status}</td>
                              <td>
                                {payment.returnCode == '00'
                                  ? 'Capturado'
                                  : `Erro: ${payment.returnCode}`}
                              </td>
                            </tr>
                          ))}
                          {installment.settledBilling && (
                            <tr key={`four-tr-${index}`}>
                              <td>Alterada</td>
                              <td>{formatDateTime(installment.settledBilling.createdAt)}</td>
                              <td>
                                ****
                                {installment?.creditCardPayments?.[index]?.creditCard
                                  ?.lastNumbers ?? ''}
                              </td>
                              <td>
                                {installment?.creditCardPayments?.[index]?.creditCard?.brand
                                  ?.description ?? ''}
                              </td>
                              <td>{installment.settledBilling.status}</td>
                              <td>{installment?.settledBilling?.paymentOption?.name}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
      {!isRecurrencePayment && (
        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-5'>
          <thead>
            <tr className='fw-bold text-muted'>
              <th className='min-w-120px'>Preço</th>
              <th className='min-w-120px'>{isBoleto ? 'Dt de vencimento' : 'Dt processamento'}</th>
              <th className='min-w-120px'>Parcela</th>
              <th className='min-w-120px'>Status</th>
              <th className='min-w-120px'>Ações</th>
            </tr>
          </thead>
          <tbody>
            {billing?.creditCardPayments?.length
              ? billing?.creditCardPayments?.map((installment, index) => {
                  const badgeStatus = badColor.find(
                    (badColor) => badColor.name === installment.status
                  )
                  return (
                    <React.Fragment key={`reactFragmentAccordionItem-${index}`}>
                      <tr key={`installmentsAccordion${index}`}>
                        <td>
                          <b>{formatMask(installment.amount)}</b>
                        </td>
                        <td>
                          <b>{formatDate(installment.createdAt)}</b>
                        </td>
                        <td>
                          <b>{installment.installments}</b>
                        </td>
                        <td>
                          <span className={badgeStatus?.color ?? 'badge badge-secondary'}>
                            {installment.status}
                          </span>
                        </td>
                        <td>
                          <a
                            href='#'
                            className='btn btn-color-muted btn-active-color-danger menu-dropdown'
                            data-kt-menu-trigger='click'
                            data-kt-menu-placement='bottom-end'
                          >
                            <i className='ki-duotone ki-gear me-5 fs-1'>
                              <span className='path1'></span>
                              <span className='path2'></span>
                              <span className='path3'></span>
                              <span className='path4'></span>
                            </i>
                          </a>
                          <div
                            className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-danger fw-semibold fs-7 w-auto py-2'
                            data-kt-menu='true'
                            data-popper-placement='bottom-end'
                          >
                            <div className='menu-item px-3'>
                              {options.map(
                                (
                                  option: {
                                    value: string
                                    label: string
                                    status: number[]
                                  },
                                  index
                                ) =>
                                  option.status.includes(99) && (
                                    <span
                                      key={`billingDetailContentInstallmentsOption${index}`}
                                      className='menu-link px-3'
                                      onClick={() => verifyAction(option.value, installment, null)}
                                      style={{
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {option.label}
                                    </span>
                                  )
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>

                      <tr
                        className={`accordion-collapse collapse ${
                          openViewCollapse == String(installment.id) ? 'show' : ''
                        }`}
                        key={`second-tr-${index}`}
                      >
                        <td colSpan={6}>
                          {loading && <PageOverlayer />}
                          {!loading && dataViewCielo && (
                            <div
                              className='border border-gray-300 border-dashed rounded'
                              style={{backgroundColor: 'rgb(249, 234, 233)'}}
                            >
                              <div className='container m-5'>
                                <div className='d-flex flex-wrap py-5'>
                                  <div className='flex-equal me-5'>
                                    <table className='table table-flush fw-semibold gy-1'>
                                      <tbody>
                                        <tr>
                                          <td className='text-black fw-bold min-w-125px w-125px'>
                                            Tid
                                          </td>
                                          <td className='text-gray-800'>{dataViewCielo?.Tid}</td>
                                        </tr>
                                        <tr>
                                          <td className='text-black fw-bold min-w-125px w-125px'>
                                            Número do cartão
                                          </td>
                                          <td className='text-gray-800'>
                                            {dataViewCielo?.creditCard.CardNumber}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className='text-black fw-bold min-w-125px w-125px'>
                                            Titular
                                          </td>
                                          <td className='text-gray-800'>
                                            {dataViewCielo?.creditCard.Holder}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className='text-black fw-bold min-w-125px w-125px'>
                                            Validade
                                          </td>
                                          <td className='text-gray-800'>
                                            {dataViewCielo?.creditCard.ExpirationDate}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className='text-black fw-bold min-w-125px w-225px'>
                                            Bandeira
                                          </td>
                                          <td className='text-gray-800'>
                                            {dataViewCielo?.creditCard.Brand}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className='text-black fw-bold min-w-125px w-125px'>
                                            Referência de conta de pagamento
                                          </td>
                                          <td className='text-gray-800'>
                                            {dataViewCielo?.creditCard.PaymentAccountReference}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })
              : billing?.installments.map((installment, index) => {
                  const badgeStatus = badColor.find(
                    (badColor) => badColor.name === installment.status
                  )
                  return (
                    <React.Fragment key={`installment-${index}`}>
                      <tr key={`installmentsAccordion${index}`}>
                        <td>
                          <b>{formatMask(installment.amount)}</b>
                        </td>
                        <td>
                          <b>
                            {formatDate(
                              isBoleto
                                ? installment.dueDate.toString()
                                : installment.createdAt.toString()
                            )}
                          </b>
                        </td>
                        <td>
                          <b>{installment.installment}</b>
                        </td>
                        <td>
                          <span className={badgeStatus?.color ?? 'badge badge-secondary'}>
                            {installment.status}
                          </span>
                          {installment.settledBilling && (
                            <div className='text-muted fs-8 mt-1'>
                              Cobrança #{installment.settledBilling.id} -{' '}
                              {installment.settledBilling.paymentOption?.name} (
                              {installment.settledBilling.status})
                            </div>
                          )}
                        </td>
                        <td>
                          {billing?.paymentOption?.id !== 3 &&
                            (installmentPayableStatus.includes(installment.statusId) ||
                              canCopyInstallmentPaymentLink(installment)) && (
                              <React.Fragment key={`installmentStatus-${index}`}>
                                <a
                                  href='#'
                                  className='btn btn-color-muted btn-active-color-danger menu-dropdown'
                                  data-kt-menu-trigger='click'
                                  data-kt-menu-placement='bottom-end'
                                >
                                  <i className='ki-duotone ki-gear me-5 fs-1'>
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                    <span className='path3'></span>
                                    <span className='path4'></span>
                                  </i>
                                </a>
                                <div
                                  className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-danger fw-semibold fs-7 w-auto py-2'
                                  data-kt-menu='true'
                                  data-popper-placement='bottom-end'
                                >
                                  {canCopyInstallmentPaymentLink(installment) && (
                                    <div className='menu-item px-3'>
                                      <span
                                        className='menu-link px-3'
                                        onClick={() =>
                                          verifyAction('paymentLink', installment, billing)
                                        }
                                        style={{
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        Copiar link de pagamento
                                      </span>
                                    </div>
                                  )}
                                  {installmentPayableStatus.includes(installment.statusId) && (
                                    <div className='menu-item px-3'>
                                      <span
                                        key={`installmentsOption${index}`}
                                        className={`menu-link px-3 ${
                                          canPayInstallment ? '' : 'text-muted'
                                        }`}
                                        title={
                                          !isAuthorizedToUpdateInstallment
                                            ? 'Você não tem permissão para atualizar o status da parcela'
                                            : !isBillingPaidOrExpired
                                            ? 'O status atual da cobrança não permite o pagamento da parcela'
                                            : undefined
                                        }
                                        onClick={() =>
                                          canPayInstallment &&
                                          verifyAction(
                                            'paymentInstallmentStatus',
                                            installment,
                                            billing
                                          )
                                        }
                                        style={{
                                          whiteSpace: 'nowrap',
                                          ...(!canPayInstallment && {
                                            cursor: 'not-allowed',
                                            opacity: 0.5,
                                          }),
                                        }}
                                      >
                                        Pagar
                                      </span>
                                    </div>
                                  )}
                                  {canChangeInstallmentPayment &&
                                    installment.installment === firstOpenInstallment && (
                                      <div className='menu-item px-3'>
                                        <span
                                          className='menu-link px-3'
                                          onClick={() =>
                                            verifyAction(
                                              'changeInstallmentPayment',
                                              installment,
                                              billing
                                            )
                                          }
                                          style={{
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          Alterar forma de pagamento
                                        </span>
                                      </div>
                                    )}
                                </div>
                              </React.Fragment>
                            )}
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })}
          </tbody>
        </table>
      )}
    </>
  )
}

export default BillingDetailContent
